import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormLabel,
  Divider
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ImageUploader from './ImageUploader'
import axios from 'axios'

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

type DragState =
  | {
      mode: 'move'
      startPointer: { x: number; y: number }
      startCropArea: CropArea
    }
  | {
      mode: 'resize'
      handle: ResizeHandle
      handleOffset: { x: number; y: number }
      startPointer: { x: number; y: number }
      startCropArea: CropArea
    }

// #T# Mask presets config - dla auto-crop z maską
// Note: Backend obsługuje tylko SAM3. BodyPix jest client-side w MaskTool.
const MASK_PRESETS = {
  head: { label: 'Głowa', engine: 'sam3' as const, prompt: 'head' },
  'head-shoulders': { label: 'Głowa + ramiona', engine: 'sam3' as const, prompt: 'head and shoulders' },
  torso: { label: 'Tors', engine: 'sam3' as const, prompt: 'torso' },
  hands: { label: 'Dłonie', engine: 'sam3' as const, prompt: 'hands' },
  feet: { label: 'Stopy', engine: 'sam3' as const, prompt: 'feet' },
  'hands-head': { label: 'Dłonie + głowa', engine: 'sam3' as const, prompt: 'hands and head' },
  'hands-head-feet': { label: 'Dłonie + głowa + stopy', engine: 'sam3' as const, prompt: 'hands and head and feet' },
  custom: { label: 'Własny prompt...', engine: 'sam3' as const, prompt: '' }
}

const MARGIN_VALUES = {
  tight: 5,
  normal: 20,
  loose: 50
}

type MaskPresetKey = keyof typeof MASK_PRESETS
type MarginPreset = keyof typeof MARGIN_VALUES

const ImageCropper: React.FC = () => {
  // Existing state
  const [sourceImage, setSourceImage] = useState<string>('')
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null)
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 400, height: 300 })
  const [dragState, setDragState] = useState<DragState | null>(null)
  const [canvasCursor, setCanvasCursor] = useState<string>('default')
  const [lockAspectRatio, setLockAspectRatio] = useState<boolean>(false)
  const [croppedImage, setCroppedImage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  // #W# New state for mask integration
  const [useMask, setUseMask] = useState<boolean>(false)
  const [maskPreset, setMaskPreset] = useState<MaskPresetKey>('hands-head-feet') // Default to main use case
  const [customPrompt, setCustomPrompt] = useState<string>('')
  const [marginPreset, setMarginPreset] = useState<MarginPreset>('normal')
  const [generatedMask, setGeneratedMask] = useState<string | null>(null)
  const [showMaskOverlay, setShowMaskOverlay] = useState<boolean>(true)
  const [isGeneratingMask, setIsGeneratingMask] = useState<boolean>(false)

  // #T# calculateBoundingBox - znajduje bbox białych pikseli na masce z downsampling
  const calculateBoundingBox = (
    maskBase64: string,
    margin: number = 0
  ): Promise<CropArea | null> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        // Original canvas
        const origCanvas = document.createElement('canvas')
        origCanvas.width = img.width
        origCanvas.height = img.height

        // Scan canvas (possibly downscaled for performance)
        let scanCanvas = origCanvas
        let scale = 1.0
        const maxDim = 1024 // Performance optimization

        if (img.width > maxDim || img.height > maxDim) {
          scale = Math.min(maxDim / img.width, maxDim / img.height)
          scanCanvas = document.createElement('canvas')
          scanCanvas.width = Math.floor(img.width * scale)
          scanCanvas.height = Math.floor(img.height * scale)
          const scanCtx = scanCanvas.getContext('2d')
          if (scanCtx) {
            scanCtx.drawImage(img, 0, 0, scanCanvas.width, scanCanvas.height)
          }
        } else {
          const ctx = origCanvas.getContext('2d')
          if (ctx) {
            ctx.drawImage(img, 0, 0)
          }
          scanCanvas = origCanvas
        }

        // Scan pixels
        const ctx = scanCanvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Cannot get canvas context'))
          return
        }

        const imageData = ctx.getImageData(0, 0, scanCanvas.width, scanCanvas.height)
        const pixels = imageData.data

        let minX = scanCanvas.width
        let minY = scanCanvas.height
        let maxX = 0
        let maxY = 0
        let foundWhite = false
        let whitePixelCount = 0

        // Scan pixels (RGBA format)
        for (let y = 0; y < scanCanvas.height; y++) {
          for (let x = 0; x < scanCanvas.width; x++) {
            const idx = (y * scanCanvas.width + x) * 4
            const r = pixels[idx]
            const g = pixels[idx + 1]
            const b = pixels[idx + 2]
            const gray = (r + g + b) / 3

            if (gray > 128) {
              foundWhite = true
              whitePixelCount++
              minX = Math.min(minX, x)
              minY = Math.min(minY, y)
              maxX = Math.max(maxX, x)
              maxY = Math.max(maxY, y)
            }
          }
        }

        if (!foundWhite) {
          resolve(null) // Empty mask
          return
        }

        // Check white pixel ratio
        const totalPixels = scanCanvas.width * scanCanvas.height
        const whiteRatio = whitePixelCount / totalPixels

        if (whiteRatio < 0.01) {
          console.warn('Mask area very small (< 1%)')
        }

        // Scale back to original dimensions
        const x = Math.floor(minX / scale)
        const y = Math.floor(minY / scale)
        const width = Math.floor((maxX - minX) / scale)
        const height = Math.floor((maxY - minY) / scale)

        // Apply margin with bounds checking
        const marginX = Math.max(0, x - margin)
        const marginY = Math.max(0, y - margin)
        const marginWidth = Math.min(img.width - marginX, width + 2 * margin)
        const marginHeight = Math.min(img.height - marginY, height + 2 * margin)

        resolve({
          x: marginX,
          y: marginY,
          width: marginWidth,
          height: marginHeight
        })
      }

      img.onerror = () => reject(new Error('Failed to load mask image'))
      img.src = maskBase64.startsWith('data:')
        ? maskBase64
        : `data:image/png;base64,${maskBase64}`
    })
  }

  // #W# generateMaskAndSetCrop - generuje maskę i ustawia crop area
  const handleGenerateMaskAndSetCrop = async () => {
    if (!sourceImage || !imageSize) {
      setError('Proszę najpierw załadować obraz')
      return
    }

    setIsGeneratingMask(true)
    setError('')

    try {
      const preset = MASK_PRESETS[maskPreset]
      const prompt = maskPreset === 'custom' ? customPrompt : preset.prompt

      if (!prompt) {
        setError('Proszę wprowadzić prompt dla własnego maskowania')
        setIsGeneratingMask(false)
        return
      }

      const token = localStorage.getItem('token')
      const response = await axios.post(
        '/api/photo-tools/mask',
        {
          image: sourceImage,
          engine: preset.engine,
          prompt: prompt,
          invert: false
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 45000 // 45s timeout
        }
      )

      if (response.data.status === 'success') {
        const maskBase64 = response.data.mask_image
        setGeneratedMask(`data:image/png;base64,${maskBase64}`)

        // Calculate bounding box from mask
        const margin = MARGIN_VALUES[marginPreset]
        const bbox = await calculateBoundingBox(
          `data:image/png;base64,${maskBase64}`,
          margin
        )

        if (!bbox) {
          setError('Nie znaleziono obszaru do przycięcia. Spróbuj innego promptu lub presetu.')
        } else {
          // Set crop area automatically
          setCropArea(bbox)
        }
      } else {
        setError(response.data.error || 'Nie udało się wygenerować maski')
      }
    } catch (err: any) {
      console.error('Mask generation error:', err)
      if (err.code === 'ECONNABORTED') {
        setError('Przekroczono limit czasu generowania maski (45s). Spróbuj BodyPix (szybszy).')
      } else {
        setError(err.response?.data?.error || 'Wystąpił błąd podczas generowania maski')
      }
    } finally {
      setIsGeneratingMask(false)
    }
  }

  const handleImageUpload = (imageData: string, file: File) => {
    setSourceImage(imageData)
    setSourceFile(file)
    setCroppedImage('')
    setError('')

    // Load image to get dimensions
    const img = new Image()
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height })
      // Set initial crop area (center, 50% of image)
      const initialWidth = Math.floor(img.width * 0.5)
      const initialHeight = Math.floor(img.height * 0.5)
      setCropArea({
        x: Math.floor((img.width - initialWidth) / 2),
        y: Math.floor((img.height - initialHeight) / 2),
        width: initialWidth,
        height: initialHeight
      })
    }
    img.src = imageData
  }

  const handleCropChange = (field: keyof CropArea, value: string) => {
    const numValue = parseInt(value) || 0

    if (lockAspectRatio && imageSize) {
      const aspectRatio = cropArea.width / cropArea.height

      if (field === 'width') {
        setCropArea(normalizeCropArea({
          ...cropArea,
          width: numValue,
          height: Math.round(numValue / aspectRatio)
        }, imageSize))
      } else if (field === 'height') {
        setCropArea(normalizeCropArea({
          ...cropArea,
          height: numValue,
          width: Math.round(numValue * aspectRatio)
        }, imageSize))
      } else {
        setCropArea(normalizeCropArea({ ...cropArea, [field]: numValue }, imageSize))
      }
    } else {
      setCropArea(imageSize ? normalizeCropArea({ ...cropArea, [field]: numValue }, imageSize) : { ...cropArea, [field]: numValue })
    }
  }

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

  const normalizeCropArea = (area: CropArea, size: { width: number; height: number }): CropArea => {
    const minSize = 10
    const width = clamp(Math.round(area.width), minSize, size.width)
    const height = clamp(Math.round(area.height), minSize, size.height)
    const x = clamp(Math.round(area.x), 0, size.width - width)
    const y = clamp(Math.round(area.y), 0, size.height - height)
    return { x, y, width, height }
  }

  const getCursorForHandle = (handle: ResizeHandle | 'move' | null): string => {
    if (!handle) return 'default'
    if (handle === 'move') return 'grab'
    if (handle === 'n' || handle === 's') return 'ns-resize'
    if (handle === 'e' || handle === 'w') return 'ew-resize'
    if (handle === 'ne' || handle === 'sw') return 'nesw-resize'
    return 'nwse-resize'
  }

  const getHandleAtPoint = (
    x: number,
    y: number,
    area: CropArea,
    tolX: number,
    tolY: number
  ): ResizeHandle | 'move' | null => {
    const left = area.x
    const top = area.y
    const right = area.x + area.width
    const bottom = area.y + area.height

    const nearLeft = Math.abs(x - left) <= tolX
    const nearRight = Math.abs(x - right) <= tolX
    const nearTop = Math.abs(y - top) <= tolY
    const nearBottom = Math.abs(y - bottom) <= tolY

    const withinX = x >= left - tolX && x <= right + tolX
    const withinY = y >= top - tolY && y <= bottom + tolY

    // Corners first (higher priority)
    if (nearLeft && nearTop) return 'nw'
    if (nearRight && nearTop) return 'ne'
    if (nearLeft && nearBottom) return 'sw'
    if (nearRight && nearBottom) return 'se'

    // Edges
    if (nearTop && withinX) return 'n'
    if (nearBottom && withinX) return 's'
    if (nearLeft && withinY) return 'w'
    if (nearRight && withinY) return 'e'

    // Inside = move
    if (x >= left && x <= right && y >= top && y <= bottom) return 'move'

    return null
  }

  const getPointerContext = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !imageSize) return null

    const rect = canvasRef.current.getBoundingClientRect()
    const scaleX = imageSize.width / rect.width
    const scaleY = imageSize.height / rect.height
    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY

    // Hit target ~10px in CSS pixels
    const tolX = 10 * scaleX
    const tolY = 10 * scaleY

    return { x, y, tolX, tolY }
  }

  const computeResizedArea = (
    handle: ResizeHandle,
    start: CropArea,
    pointerX: number,
    pointerY: number,
    size: { width: number; height: number },
    keepAspect: boolean
  ): CropArea => {
    const minSize = 10

    if (keepAspect) {
      const ratio = start.width / start.height
      const centerX = start.x + start.width / 2
      const centerY = start.y + start.height / 2

      // Corner handles: opposite corner is anchor
      if (handle === 'nw' || handle === 'ne' || handle === 'sw' || handle === 'se') {
        const startLeft = start.x
        const startRight = start.x + start.width
        const startTop = start.y
        const startBottom = start.y + start.height

        let anchorX = startLeft
        let anchorY = startTop
        if (handle === 'nw') {
          anchorX = startRight
          anchorY = startBottom
        } else if (handle === 'ne') {
          anchorX = startLeft
          anchorY = startBottom
        } else if (handle === 'sw') {
          anchorX = startRight
          anchorY = startTop
        } else if (handle === 'se') {
          anchorX = startLeft
          anchorY = startTop
        }

        const desiredWidth = Math.abs(pointerX - anchorX)
        const desiredHeight = Math.abs(pointerY - anchorY)

        // Choose the dominant axis, then enforce ratio
        let width = desiredWidth
        let height = desiredHeight
        if (desiredWidth / Math.max(desiredHeight, 1e-6) > ratio) {
          width = desiredHeight * ratio
          height = desiredHeight
        } else {
          width = desiredWidth
          height = desiredWidth / ratio
        }

        // Max possible size given anchor and direction
        let maxWidth = size.width
        let maxHeight = size.height
        if (handle === 'nw') {
          maxWidth = anchorX
          maxHeight = anchorY
        } else if (handle === 'ne') {
          maxWidth = size.width - anchorX
          maxHeight = anchorY
        } else if (handle === 'sw') {
          maxWidth = anchorX
          maxHeight = size.height - anchorY
        } else if (handle === 'se') {
          maxWidth = size.width - anchorX
          maxHeight = size.height - anchorY
        }

        // Clamp while keeping ratio
        const maxByWidth = clamp(maxWidth, minSize, maxWidth)
        const maxByHeight = clamp(maxHeight, minSize, maxHeight)
        width = clamp(width, minSize, maxByWidth)
        height = width / ratio
        if (height > maxByHeight) {
          height = maxByHeight
          width = height * ratio
        }

        let left = anchorX
        let top = anchorY
        let right = anchorX
        let bottom = anchorY

        if (handle === 'nw') {
          left = anchorX - width
          top = anchorY - height
          right = anchorX
          bottom = anchorY
        } else if (handle === 'ne') {
          left = anchorX
          top = anchorY - height
          right = anchorX + width
          bottom = anchorY
        } else if (handle === 'sw') {
          left = anchorX - width
          top = anchorY
          right = anchorX
          bottom = anchorY + height
        } else if (handle === 'se') {
          left = anchorX
          top = anchorY
          right = anchorX + width
          bottom = anchorY + height
        }

        return normalizeCropArea(
          {
            x: left,
            y: top,
            width: right - left,
            height: bottom - top
          },
          size
        )
      }

      // Edge handles with aspect ratio: resize on one axis, keep the other centered
      if (handle === 'e' || handle === 'w') {
        const anchorX = handle === 'e' ? start.x : start.x + start.width
        const maxWidth = handle === 'e' ? size.width - anchorX : anchorX
        let width = clamp(handle === 'e' ? pointerX - anchorX : anchorX - pointerX, minSize, maxWidth)
        let height = width / ratio

        if (height > size.height) {
          height = size.height
          width = Math.min(height * ratio, maxWidth)
          height = width / ratio
        }

        const top = clamp(centerY - height / 2, 0, size.height - height)
        const x = handle === 'e' ? anchorX : anchorX - width

        return normalizeCropArea(
          {
            x,
            y: top,
            width,
            height
          },
          size
        )
      }

      // 'n' or 's'
      const anchorY = handle === 's' ? start.y : start.y + start.height
      const maxHeight = handle === 's' ? size.height - anchorY : anchorY
      let height = clamp(handle === 's' ? pointerY - anchorY : anchorY - pointerY, minSize, maxHeight)
      let width = height * ratio

      if (width > size.width) {
        width = size.width
        height = width / ratio
        height = Math.min(height, maxHeight)
        width = height * ratio
      }

      const left = clamp(centerX - width / 2, 0, size.width - width)
      const y = handle === 's' ? anchorY : anchorY - height

      return normalizeCropArea(
        {
          x: left,
          y,
          width,
          height
        },
        size
      )
    }

    // No aspect ratio lock: freeform resizing by edges
    let left = start.x
    let top = start.y
    let right = start.x + start.width
    let bottom = start.y + start.height

    const hasW = handle.includes('w')
    const hasE = handle.includes('e')
    const hasN = handle.includes('n')
    const hasS = handle.includes('s')

    if (hasW) left = pointerX
    if (hasE) right = pointerX
    if (hasN) top = pointerY
    if (hasS) bottom = pointerY

    // Clamp and enforce minimum size
    if (hasW) left = clamp(left, 0, right - minSize)
    if (hasE) right = clamp(right, left + minSize, size.width)
    if (hasN) top = clamp(top, 0, bottom - minSize)
    if (hasS) bottom = clamp(bottom, top + minSize, size.height)

    return {
      x: Math.round(left),
      y: Math.round(top),
      width: Math.round(right - left),
      height: Math.round(bottom - top)
    }
  }

  const getHandleAnchorPoint = (handle: ResizeHandle, area: CropArea) => {
    const left = area.x
    const top = area.y
    const right = area.x + area.width
    const bottom = area.y + area.height
    const midX = area.x + area.width / 2
    const midY = area.y + area.height / 2

    switch (handle) {
      case 'nw':
        return { x: left, y: top }
      case 'ne':
        return { x: right, y: top }
      case 'sw':
        return { x: left, y: bottom }
      case 'se':
        return { x: right, y: bottom }
      case 'n':
        return { x: midX, y: top }
      case 's':
        return { x: midX, y: bottom }
      case 'w':
        return { x: left, y: midY }
      case 'e':
        return { x: right, y: midY }
    }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getPointerContext(e)
    if (!ctx || !imageSize || !canvasRef.current) return

    const handle = getHandleAtPoint(ctx.x, ctx.y, cropArea, ctx.tolX, ctx.tolY)
    if (!handle) return

    e.preventDefault()
    canvasRef.current.setPointerCapture(e.pointerId)

    if (handle === 'move') {
      setDragState({
        mode: 'move',
        startPointer: { x: ctx.x, y: ctx.y },
        startCropArea: cropArea
      })
      setCanvasCursor('grabbing')
      return
    }

    const handlePoint = getHandleAnchorPoint(handle, cropArea)
    setDragState({
      mode: 'resize',
      handle,
      handleOffset: {
        x: handlePoint.x - ctx.x,
        y: handlePoint.y - ctx.y
      },
      startPointer: { x: ctx.x, y: ctx.y },
      startCropArea: cropArea
    })
    setCanvasCursor(getCursorForHandle(handle))
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getPointerContext(e)
    if (!ctx || !imageSize) return

    if (!dragState) {
      const hoverHandle = getHandleAtPoint(ctx.x, ctx.y, cropArea, ctx.tolX, ctx.tolY)
      const nextCursor = getCursorForHandle(hoverHandle)
      if (nextCursor !== canvasCursor) setCanvasCursor(nextCursor)
      return
    }

    e.preventDefault()

    if (dragState.mode === 'move') {
      const dx = ctx.x - dragState.startPointer.x
      const dy = ctx.y - dragState.startPointer.y
      const next = normalizeCropArea(
        {
          ...dragState.startCropArea,
          x: dragState.startCropArea.x + dx,
          y: dragState.startCropArea.y + dy
        },
        imageSize
      )
      setCropArea(next)
      return
    }

    const pointerX = ctx.x + dragState.handleOffset.x
    const pointerY = ctx.y + dragState.handleOffset.y
    const next = computeResizedArea(
      dragState.handle,
      dragState.startCropArea,
      pointerX,
      pointerY,
      imageSize,
      lockAspectRatio
    )
    setCropArea(next)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (canvasRef.current && canvasRef.current.hasPointerCapture(e.pointerId)) {
      canvasRef.current.releasePointerCapture(e.pointerId)
    }
    setDragState(null)

    const ctx = getPointerContext(e)
    if (!ctx) {
      setCanvasCursor('default')
      return
    }
    const hoverHandle = getHandleAtPoint(ctx.x, ctx.y, cropArea, ctx.tolX, ctx.tolY)
    setCanvasCursor(getCursorForHandle(hoverHandle))
  }

  const handlePointerLeave = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (dragState) return
    const ctx = getPointerContext(e)
    if (!ctx) {
      setCanvasCursor('default')
      return
    }
    const hoverHandle = getHandleAtPoint(ctx.x, ctx.y, cropArea, ctx.tolX, ctx.tolY)
    setCanvasCursor(getCursorForHandle(hoverHandle))
  }

  const handleApplyCrop = async () => {
    if (!sourceImage || !imageSize) {
      setError('Proszę załadować obraz')
      return
    }

    // Validate crop area
    if (cropArea.x < 0 || cropArea.y < 0 || cropArea.width <= 0 || cropArea.height <= 0) {
      setError('Nieprawidłowe wymiary przycinania')
      return
    }

    if (cropArea.x + cropArea.width > imageSize.width || cropArea.y + cropArea.height > imageSize.height) {
      setError('Obszar przycinania wykracza poza obraz')
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        '/api/photo-tools/crop',
        {
          image: sourceImage,
          x: cropArea.x,
          y: cropArea.y,
          width: cropArea.width,
          height: cropArea.height
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data.status === 'success') {
        setCroppedImage(`data:image/png;base64,${response.data.cropped_image}`)
      } else {
        setError(response.data.error || 'Nie udało się przyciąć obrazu')
      }
    } catch (err: any) {
      console.error('Crop error:', err)
      setError(err.response?.data?.error || 'Wystąpił błąd podczas przycinania')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!croppedImage) return

    const link = document.createElement('a')
    link.download = 'cropped-image.png'
    link.href = croppedImage
    link.click()
  }

  const handleReset = () => {
    setSourceImage('')
    setSourceFile(null)
    setImageSize(null)
    setCroppedImage('')
    setError('')
    setCropArea({ x: 0, y: 0, width: 400, height: 300 })
    setLockAspectRatio(false)
    setDragState(null)
    setCanvasCursor('default')
    // Reset mask state
    setUseMask(false)
    setMaskPreset('hands-head-feet')
    setCustomPrompt('')
    setMarginPreset('normal')
    setGeneratedMask(null)
    setShowMaskOverlay(true)
    setIsGeneratingMask(false)
  }

  // #T# Draw crop preview on canvas + mask overlay
  useEffect(() => {
    if (!canvasRef.current || !imageRef.current || !sourceImage || !imageSize) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = imageRef.current

    // Set canvas size
    canvas.width = imageSize.width
    canvas.height = imageSize.height

    // Draw image
    ctx.drawImage(img, 0, 0)

    // Draw mask overlay if enabled
    if (generatedMask && showMaskOverlay) {
      const maskImg = new Image()
      maskImg.onload = () => {
        // Create temporary canvas for mask
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = imageSize.width
        tempCanvas.height = imageSize.height
        const tempCtx = tempCanvas.getContext('2d')
        if (!tempCtx) return

        tempCtx.drawImage(maskImg, 0, 0, imageSize.width, imageSize.height)
        const maskData = tempCtx.getImageData(0, 0, imageSize.width, imageSize.height)

        // Draw green overlay where mask is white
        ctx.save()
        ctx.globalAlpha = 0.3
        ctx.fillStyle = '#00FF00' // Green

        const pixels = maskData.data
        for (let y = 0; y < imageSize.height; y++) {
          for (let x = 0; x < imageSize.width; x++) {
            const idx = (y * imageSize.width + x) * 4
            const gray = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3
            if (gray > 128) {
              ctx.fillRect(x, y, 1, 1)
            }
          }
        }

        ctx.restore()

        // Continue with crop area rendering after mask
        drawCropAreaOverlay()
      }
      maskImg.src = generatedMask
    } else {
      drawCropAreaOverlay()
    }

    function drawCropAreaOverlay() {
      if (!ctx || !canvas) return

      // Draw overlay (darken outside crop area)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Clear crop area
      ctx.clearRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height)
      ctx.drawImage(img, cropArea.x, cropArea.y, cropArea.width, cropArea.height, cropArea.x, cropArea.y, cropArea.width, cropArea.height)

      // Draw crop border
      ctx.strokeStyle = '#2196F3'
      ctx.lineWidth = 3
      ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height)

      // Draw corner handles
      const handleSize = 10
      ctx.fillStyle = '#2196F3'
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize)
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize)
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize)
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize)

      // Draw edge handles (midpoints)
      const midX = cropArea.x + cropArea.width / 2
      const midY = cropArea.y + cropArea.height / 2
      ctx.fillRect(midX - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize) // N
      ctx.fillRect(midX - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize) // S
      ctx.fillRect(cropArea.x - handleSize / 2, midY - handleSize / 2, handleSize, handleSize) // W
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, midY - handleSize / 2, handleSize, handleSize) // E
    }
  }, [sourceImage, imageSize, cropArea, generatedMask, showMaskOverlay])

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!sourceImage && (
        <ImageUploader
          label="Obraz do przycięcia"
          onImageUpload={handleImageUpload}
          currentImage={sourceImage}
        />
      )}

      {sourceImage && !croppedImage && imageSize && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Przeciągnij, aby przesunąć obszar. Złap za krawędzie lub narożniki, aby zmienić rozmiar.
              </Typography>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <img
                  ref={imageRef}
                  src={sourceImage}
                  alt="Source"
                  style={{ display: 'none' }}
                />
                <canvas
                  ref={canvasRef}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  onPointerLeave={handlePointerLeave}
                  style={{
                    width: '100%',
                    height: 'auto',
                    cursor: canvasCursor,
                    border: '1px solid #ddd',
                    touchAction: 'none'
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Oryginalne wymiary: {imageSize.width} x {imageSize.height}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* #W# Mask Options Section */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useMask}
                    onChange={(e) => setUseMask(e.target.checked)}
                  />
                }
                label="Auto-crop z maską"
              />

              {useMask && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Co wyciąć?</InputLabel>
                    <Select
                      value={maskPreset}
                      label="Co wyciąć?"
                      onChange={(e) => setMaskPreset(e.target.value as MaskPresetKey)}
                    >
                      {Object.entries(MASK_PRESETS).map(([key, preset]) => (
                        <MenuItem key={key} value={key}>
                          {preset.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {maskPreset === 'custom' && (
                    <TextField
                      label="Własny prompt"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      placeholder="np. head and torso"
                      fullWidth
                      size="small"
                    />
                  )}

                  <FormControl component="fieldset" size="small">
                    <FormLabel component="legend">Margines</FormLabel>
                    <RadioGroup
                      value={marginPreset}
                      onChange={(e) => setMarginPreset(e.target.value as MarginPreset)}
                    >
                      <FormControlLabel value="tight" control={<Radio size="small" />} label="Ciasny (5px)" />
                      <FormControlLabel value="normal" control={<Radio size="small" />} label="Normalny (20px)" />
                      <FormControlLabel value="loose" control={<Radio size="small" />} label="Luźny (50px)" />
                    </RadioGroup>
                  </FormControl>

                  <Button
                    variant="outlined"
                    startIcon={isGeneratingMask ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                    onClick={handleGenerateMaskAndSetCrop}
                    disabled={isGeneratingMask || !sourceImage}
                    fullWidth
                  >
                    {isGeneratingMask ? 'Generuję maskę...' : 'Wygeneruj i ustaw crop'}
                  </Button>

                  {generatedMask && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showMaskOverlay}
                          onChange={(e) => setShowMaskOverlay(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Pokaż maskę na obrazie"
                    />
                  )}
                </Box>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Wymiary przycinania</Typography>

              <TextField
                label="X (od lewej)"
                type="number"
                value={cropArea.x}
                onChange={(e) => handleCropChange('x', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Y (od góry)"
                type="number"
                value={cropArea.y}
                onChange={(e) => handleCropChange('y', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Szerokość"
                type="number"
                value={cropArea.width}
                onChange={(e) => handleCropChange('width', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Wysokość"
                type="number"
                value={cropArea.height}
                onChange={(e) => handleCropChange('height', e.target.value)}
                fullWidth
                size="small"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockAspectRatio}
                    onChange={(e) => setLockAspectRatio(e.target.checked)}
                  />
                }
                label="Zachowaj proporcje"
              />

              <Button
                variant="contained"
                onClick={handleApplyCrop}
                disabled={loading}
                fullWidth
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Przytnij'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                fullWidth
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {croppedImage && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Oryginał
                </Typography>
                <Box
                  component="img"
                  src={sourceImage}
                  alt="Original"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain'
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Przycięty ({cropArea.width} x {cropArea.height})
                </Typography>
                <Box
                  component="img"
                  src={croppedImage}
                  alt="Cropped"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain'
                  }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Pobierz
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
            >
              Nowe przycięcie
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ImageCropper
